// The staff Sing in Solfège draws a hymn phrase on (issue #3722), from the "Sing in Solfège (listen)",
// "(sing along)" and "(result)" artboards: where each note's slot is across the staff, where a
// moment of the phrase is (the dot, the trace), which step each note sits on, where a sung pitch
// is drawn, each note's target lane, and how large the syllables can be. Pure numbers - no React, no
// DOM, no audio - like Interval Training's interval_staff_geometry.ts, whose steps it reuses (the
// clef, where do sits, the ledger lines and the stems all follow it and Pitch Match's
// staff_geometry.ts, so the three staffs place a note the same way).
//
// Across: time. Unlike Interval Training's even columns, the phrase runs in tempo and the dot
// moves through it by the clock, so each note owns a slot whose width goes with the square root of
// its length in beats. That is how engravers space a line of music: each doubling of a note's length
// buys it about 1.4 times the room, so a quarter gets visibly more than an eighth and a half more
// than a quarter, while a long note does not eat the staff the way room in proportion to its beats
// would (a dotted half three times a quarter's room, a sixteenth a quarter of it: too wide and too
// tight at once). A crowded phrase is evened out, as an engraver widens a cramped passage (see
// "Spacing" below). The notehead and its syllable stand at the left of the slot, and time maps to x
// piecewise linearly through the slots: a moment is at its slot's left edge when its note starts
// and at its right edge when the note ends, clamped to the first slot's left edge before the phrase
// and the last one's right edge after it. The artboards stand the first note at x 90, leaving room for a clef; a major
// phrase draws none, and twelve notes laid out by time need that room, so its slots run nearly the
// whole length of the lines. A minor phrase draws the clef and its key signature there (issue
// #3772, solfege_signs.ts), and its slots start after them.
//
// Down: scale steps. A note's step is do's step plus its degree, as in Interval Training: a raised
// or lowered note (fi, te, a minor phrase's me) sits on its degree's line or space, as it is
// written, its accidental or the key signature saying how it is altered. The staff's lines and
// spaces stand for the phrase's own scale: do re mi fa sol la ti in a major phrase, do re me fa sol
// le te in a minor one. A sung pitch between two notes of that scale is drawn between their steps,
// in proportion to the semitones: the staff is diatonic, so a half step (mi-fa, ti-do; re-me in
// minor) and a whole step are both one step tall, and a voice a quarter tone above mi is half way to
// fa but a quarter tone above re only a quarter of the way to mi. A scale note's own pitch therefore
// lands exactly on its notehead, and a voice on me in a minor phrase runs through the me head.
//
// A chromatic note's pitch lies between two scale notes, so its head (on its degree's line) is not
// where its pitch is drawn: fi's pitch is half way from fa's line to sol's space. The staff does not
// bend the line to the head. Its line is an honest picture of pitch, the same mapping everywhere, so
// a voice exactly on fi runs a little above fi's head and a voice on plain fa runs through the head
// but below where fi is; the note's lane is drawn at its true pitch instead (solfegeLaneRect), a
// little above a raised note's head and a little below a lowered one's, and the line on pitch runs
// along it.
//
// The notes. Each note is written as its real value (solfegeNoteValue: the phrase's beat is the
// quarter note): a filled or open head, a stem or none, flags, an augmentation dot. Flags, never
// beams, as vocal music is engraved, each on the stem's far end and to its right, hanging toward the
// head; a dot to the right of the head, in the space above when the head sits on a line. The
// current note's glyph is scaled up with its head. A chromatic note carries its sharp, flat or
// natural just left of its head (solfege_signs.ts says which), in its colour and scaled with it.
// solfegeNoteGlyph gives every part's position and its box, so the tests can hold neighbouring notes
// clear of one another at the deck's tightest spacing.
//
// Spacing. Every note needs some least room to the next (solfegeLeastRoom): far enough that nothing
// it draws - head, stem, flags, dot, accidental, at either size, the current note's larger glyph
// included - comes within the clearance of anything the next note draws, its accidental included,
// and that their syllables keep their gap; the last note, room for what it draws right of its head.
// The first note's slot starts far enough right that its accidental clears the lines' left end, and
// a minor phrase's first note clears the key signature, which takes room from the slots. Those are
// worked out from the glyphs themselves (solfegeNoteGlyph), not tuned by hand. Every note of one
// length takes the room the neediest of them needs, a longer length never less than a shorter one,
// and what is left over is shared by the square root, so a phrase with room to spare is spaced by
// the square root alone. The room an accidental needs is the exception: only the note before it
// takes that, as an engraver adds an accidental's room where it stands, so one sharp does not widen
// every note of its neighbour's length.
// When a note needs more than its share the longer notes give up their extra room first, toward
// even spacing; only then are the syllables made smaller, and only then is the staff drawn smaller
// (solfegeSpacing has the order). A staff drawn smaller is the same drawing in a wider viewBox fitted
// to the same width on screen, so everything on it - notes, syllables, lanes, the dot, the line, the
// staff lines and every stroke - shrinks together and it reads as the same staff, only smaller; the
// slots and lines run to the wider viewBox's right edge, and the count-in dot, the playhead, the line
// and the tap areas follow the slots as ever.
//
// The band. The staff is the five lines plus the first ledger line on either side and two steps of
// room beyond (steps -4 to 12, as Interval Training's band margin), so every phrase whose notes stay
// near the staff - which is how the page picks do - draws at the same height; a phrase reaching
// further out grows the band to keep two steps beyond its outermost note. The dot and the trace are
// clamped to it.

import { semitonesFromDo, SolfegeSyllable } from '../../../../../common/src/interval_training.ts';
import { Clef } from '../../../../../common/src/learn_rules.ts';
import {
  isSolfegeChromatic,
  phraseMidis,
  phraseNoteSpans,
  SolfegeMode,
  solfegeNoteSyllable,
  SolfegeNoteValue,
  solfegeNoteValue,
  SolfegePhrase,
  solfegePhraseMode,
  solfegeScaleAlteration,
} from '../../../../../common/src/sing_in_solfege.ts';
import { bandMarginSteps, clampToBand, intervalNoteStep } from '../interval/interval_staff_geometry.ts';
import { ledgerStepsFor, staffLayout, stemDirection } from '../pitch/staff_geometry.ts';
import {
  SolfegeAccidental,
  SolfegeKeySignature,
  solfegeKeySignature,
  SolfegeKeySignatureSign,
  solfegeNoteAccidental,
  SolfegeSign,
  solfegeSign,
  solfegeSignShapes,
} from './solfege_signs.ts';

// ---- Layout ----------------------------------------------------------------------------------

const stepHeight = 10; // the artboards' 20 px between lines
const haloRadius = 17;
// Above the band, room for a halo on a dot clamped to its top: the top line lands at y 60 as drawn
const bandPadding = haloRadius + 3;

const topLineStep = 8;
const bottomLineStep = 0;

// A syllable size for the phrase: the regular one and the current note's, which is larger and bold
export interface SolfegeSyllableSize {
  regular: number;
  emphasized: number;
}

export const solfegeStaffLayout = {
  width: staffLayout.width, // 350, the artboards' viewBox
  lineX1: staffLayout.lineX1,
  lineX2: staffLayout.lineX2,
  lineWidth: staffLayout.lineWidth,
  stepHeight,
  bandPadding,
  // The slots run from here to slotsX2: a dot at either end keeps its halo on the SVG
  slotsX1: 20,
  slotsX2: 330,
  noteInsetX: 9, // a notehead's centre from its slot's left edge: the notehead sits just inside it
  normalNotehead: { rx: 8, ry: 6 },
  emphasizedNotehead: { rx: 10, ry: 7.5 }, // the current note
  noteheadRotation: staffLayout.noteheadRotation, // -20 degrees
  // An open head (a half, a whole) is a ring: the hole an ellipse narrower than the head and turned
  // further, as an engraved half note's is, so the ring is thick above and below and thin at the
  // hole's ends, and reads as open at a phone's size
  openHole: { rxShare: 0.7, ryShare: 0.45, rotation: -38 },
  stemInsetX: 1, // the stem stands this far inside the notehead's right (or left) edge
  stemLength: 40,
  stemWidth: 1.8,
  // A second flag (a sixteenth's) stands this far nearer the head than the first
  flagSpacing: 8,
  // An augmentation dot's centre is this far right of the head's centre, past its right edge
  augmentationDotX: 12.5,
  augmentationDotRadius: 2.3,
  // A ledger line is centred on its notehead, at most this far either side, and stops this far
  // short of half way to a neighbouring notehead so two neighbours' ledger lines do not join, but it
  // always reaches this far past the current note's larger head either side, as an engraved ledger
  // line does past its head: in a narrow slot (an eighth's) a line cut short at half way was hidden
  // under that head, and the note floated off the staff (#3722, round 2 review). Where the two
  // rules meet, neighbouring ledger lines may touch or merge, as printed ones do.
  ledgerMaxHalfWidth: 13,
  ledgerClearance: 1,
  ledgerOverhang: 2,
  // A note's lane: a soft rounded bar one step tall across its slot, this far short of either edge
  laneHeight: stepHeight,
  laneInsetX: 1.5,
  laneOpacity: 0.16,
  // The syllables' baseline is this far below the band's foot (y 186 on the artboards), and the
  // SVG keeps room under it for descenders and a tap
  syllableBaselineOffset: 6,
  syllableRoom: 24,
  // The artboards' 14 and 16, and a step smaller for a phrase whose syllables would collide
  syllableSizes: [
    { regular: 14, emphasized: 16 },
    { regular: 12, emphasized: 14 },
  ] as SolfegeSyllableSize[],
  syllableMinGap: 3, // the least space between two neighbouring syllables
  // The least space between any part of a note and any part of the next (see "Spacing" in the header)
  clearance: 1,
  // The smallest a phrase's staff is drawn, against the artboards': below it the notes and syllables
  // would be too small to read on a phone, so a phrase needing less is not drawable
  scaleFloor: 0.8,
  // The signs (issue #3772, solfege_signs.ts). A minor phrase's clef stands this far in from the
  // lines' left end, drawn at this many units to its staff space: nine tenths of the staff's, so it
  // spans the staff as a clef does without taking more of a phone's width than it must
  clefX: 13,
  clefUnit: 18,
  // The key signature starts this far right of the clef, its accidentals drawn as large as a note's
  // and this far apart, as close as engraved signatures set them: F minor's four flats leave little
  // enough room for SHOUT ON's quick lines that every unit counts
  keySignatureGap: 3,
  keySignatureUnit: 14,
  keySignatureSpacing: 0.8,
  // The first note's leftmost part, its accidental or its head as the current note, stands at
  // least this far right of the key signature, or of the lines' left end on a plain staff
  signatureClearance: 5,
  edgeClearance: 2,
  // A normal note's accidental is drawn a little more than its notehead's height (12.5) to a staff
  // space, as engraved accidentals are sized to their heads, enough that a sharp's bars and a flat's
  // bowl read at a phone's size; it stands this far left of the head, both scaled with the current
  // note
  accidentalUnit: 14,
  accidentalGap: 1.5,
  dotRadius: 12,
  dotStrokeWidth: 3,
  haloRadius,
  haloStrokeWidth: 2,
  haloOpacity: 0.5,
  playheadWidth: 2,
};

const layout = solfegeStaffLayout;

// How wide a syllable is in ems: the wider of Jost SemiBold (measured from the app's font file)
// and Arial, the fallback, rounded up, so the collision check holds for either font and weight.
// The altered notes' syllables (issue #3772) were measured the same way.
const syllableEms: Record<SolfegeSyllable, number> = {
  do: 1.18,
  re: 0.95,
  mi: 1.08,
  fa: 0.85,
  sol: 1.28,
  la: 0.78,
  ti: 0.57,
  di: 0.88,
  ri: 0.67,
  fi: 0.6,
  si: 0.73,
  li: 0.53,
  ra: 0.92,
  me: 1.39,
  se: 1.06,
  le: 0.82,
  te: 0.85,
};

export function solfegeSyllableWidth(syllable: SolfegeSyllable, fontSize: number): number {
  return syllableEms[syllable] * fontSize;
}

// ---- The geometry of one phrase --------------------------------------------------------------

export interface SolfegeStaffBand {
  minStep: number;
  maxStep: number;
}

// A note's stretch of the staff: x1 to x2 across, while the note sounds from startMs to endMs
// (phraseNoteSpans' scale, ms from the take's start)
export interface SolfegeSlot {
  x1: number;
  x2: number;
  startMs: number;
  endMs: number;
}

export interface SolfegeStaffNote {
  index: number;
  degree: number;
  alteration: number; // from the degree's note of the major scale (SolfegeNote's), 0 for none
  chromatic: boolean; // altered beyond the phrase's scale (isSolfegeChromatic)
  accidental?: SolfegeAccidental; // the sign it carries: only a chromatic note has one
  beats: number;
  value: SolfegeNoteValue;
  syllable: SolfegeSyllable;
  midi: number;
  step: number;
  x: number; // the notehead's centre, and its syllable's
  y: number;
  laneY: number; // where its pitch is drawn: its lane's centre, the head's y but for a chromatic note
  stem: 'up' | 'down';
  ledgerSteps: number[];
  ledgerHalfWidth: number;
  slot: SolfegeSlot;
}

// A minor phrase's clef and key signature at the left of the staff (solfege_signs.ts)
export interface SolfegeStaffSignature {
  clef: SolfegeSign;
  key: SolfegeKeySignature;
  signs: (SolfegeSign & SolfegeKeySignatureSign)[];
  x2: number; // the signature's right edge, or the clef's when the key has no accidentals
}

export interface SolfegeStaffGeometry {
  clef: Clef;
  mode: SolfegeMode; // the staff's lines and spaces stand for this scale
  homeMidi: number;
  doStep: number;
  signature?: SolfegeStaffSignature; // a minor phrase's; a major phrase's plain staff has none
  band: SolfegeStaffBand;
  // The viewBox: as wide as the artboards' 350 at scale 1, wider for a phrase drawn smaller, so the
  // same width on screen holds more of it (see "Spacing" in the header)
  width: number;
  height: number; // the SVG's, in the viewBox's units
  scale: number; // how large the staff is drawn against the artboards': 1, or down to scaleFloor
  lineX2: number; // where the staff lines end, as far from the viewBox's right edge as on the artboards
  slotsX1: number; // where the first slot starts: the artboards' 20, later after a key signature
  slotsX2: number; // where the last slot ends, as far from the viewBox's right edge as on the artboards
  lengthMs: number; // when the last note ends
  notes: SolfegeStaffNote[];
  syllableY: number; // the syllables' baseline
  syllableSize: SolfegeSyllableSize;
  crowded: boolean; // the syllables took the smaller size
  // Whether every note has its least room. False only for a phrase that would need the staff drawn
  // smaller than scaleFloor: it is drawn at the floor with its notes too close, and the deck-wide
  // guard of solfege_staff_geometry.test.ts fails for it, so no such phrase reaches a singer.
  fits: boolean;
}

// How much room a note gets across, in quarters' room: the square root of its beats (see the header)
export function solfegeSlotWeight(beats: number): number {
  return Math.sqrt(beats);
}

export function solfegeStaffGeometry({
  phrase,
  homeMidi,
  clef,
}: {
  phrase: SolfegePhrase;
  homeMidi: number;
  clef: Clef;
}): SolfegeStaffGeometry {
  const spans = phraseNoteSpans(phrase);
  const midis = phraseMidis(phrase, homeMidi);
  const mode = solfegePhraseMode(phrase);
  const doStep = intervalNoteStep(homeMidi, 0, clef);
  // A raised or lowered note (issue #3772) sits on its degree's step, as written, with its own
  // syllable under it, its own pitch in `midis` and the sign it needs beside it
  const steps = phrase.notes.map((note) => intervalNoteStep(homeMidi, note.degree, clef));
  const band = {
    minStep: Math.min(bottomLineStep - 2, ...steps) - bandMarginSteps,
    maxStep: Math.max(topLineStep + 2, ...steps) + bandMarginSteps,
  };
  const signature = signatureFor(solfegeKeySignature({ mode, homeMidi, doStep, clef }), band, clef);
  const placed = phrase.notes.map((note, index): SpacedNote => ({
    beats: note.beats,
    value: solfegeNoteValue(note.beats),
    syllable: solfegeNoteSyllable(note),
    accidental: solfegeNoteAccidental({ note, mode, homeMidi, step: steps[index], clef, signature: signature?.key }),
    step: steps[index],
    y: stepYIn(band, steps[index]),
    stem: stemDirection(steps[index]),
  }));
  const slotsX1 = firstSlotX1(placed[0], signature);
  const spacing = solfegeSpacing(placed, slotsX1);
  const width = layout.width / spacing.scale;
  const slotsX2 = width - (layout.width - layout.slotsX2);
  const slots = slotsFor(
    spacing.widths,
    spans.map((span) => span.startMs),
    spans.map((span) => span.endMs),
    slotsX1,
    slotsX2,
  );
  const xs = slots.map((slot) => slot.x1 + layout.noteInsetX);
  const notes = phrase.notes.map((note, index): SolfegeStaffNote => ({
    ...placed[index],
    index,
    degree: note.degree,
    alteration: note.alteration ?? 0,
    chromatic: isSolfegeChromatic(note, mode),
    midi: midis[index],
    x: xs[index],
    laneY: stepYIn(band, clampToBand(pitchStep(homeMidi, doStep, mode, midis[index]), band)),
    ledgerSteps: ledgerStepsFor(steps[index]),
    ledgerHalfWidth: ledgerHalfWidthAt(xs, index),
    slot: slots[index],
  }));
  const syllableY = stepYIn(band, band.minStep) + layout.syllableBaselineOffset;
  return {
    clef,
    mode,
    homeMidi,
    doStep,
    signature,
    band,
    width,
    height: syllableY + layout.syllableRoom,
    scale: spacing.scale,
    lineX2: width - (layout.width - layout.lineX2),
    slotsX1,
    slotsX2,
    lengthMs: spans.length > 0 ? spans[spans.length - 1].endMs : 0,
    notes,
    syllableY,
    syllableSize: spacing.syllableSize,
    crowded: spacing.syllableSize !== layout.syllableSizes[0],
    fits: spacing.fits,
  };
}

// ---- The clef and key signature --------------------------------------------------------------

// The line each clef curls round: the treble clef's G (the second line), the bass clef's F (the fourth)
const clefLineStep: Record<Clef, number> = { treble: 2, bass: 6 };

// A minor phrase's clef and key signature laid out from the lines' left end: the clef, then each
// accidental of the signature on its own step, left to right, each clear of the one before
function signatureFor(
  key: SolfegeKeySignature | undefined,
  band: SolfegeStaffBand,
  clef: Clef,
): SolfegeStaffSignature | undefined {
  if (!key) {
    return undefined;
  }
  const clefSign = solfegeSign(
    clef === 'treble' ? 'trebleClef' : 'bassClef',
    layout.clefX,
    stepYIn(band, clefLineStep[clef]),
    layout.clefUnit,
  );
  let left = clefSign.box.x2 + layout.keySignatureGap;
  const signs = key.signs.map((sign) => {
    const unit = layout.keySignatureUnit;
    const placed = solfegeSign(
      sign.accidental,
      left - unit * solfegeSignShapes[sign.accidental].box.x1,
      stepYIn(band, sign.step),
      unit,
    );
    left = placed.box.x2 + layout.keySignatureSpacing;
    return { ...placed, ...sign };
  });
  return { clef: clefSign, key, signs, x2: signs.length > 0 ? signs[signs.length - 1].box.x2 : clefSign.box.x2 };
}

// Where the first slot starts: the artboards' slotsX1, or further right when the first note's
// leftmost part - its accidental, or its head as the current note - would otherwise come within the
// signature's clearance of a minor phrase's key signature, or the edge clearance of the lines' left
// end. A major phrase whose first note has no accidental always starts at the artboards' 20.
function firstSlotX1(first: SpacedNote | undefined, signature: SolfegeStaffSignature | undefined): number {
  if (!first) {
    return layout.slotsX1;
  }
  const glyph = solfegeNoteGlyph({ ...first, x: 0 }, true);
  const leftmost = Math.min(glyph.head.box.x1, glyph.accidental?.box.x1 ?? Infinity);
  const edge = signature ? signature.x2 + layout.signatureClearance : layout.lineX1 + layout.edgeClearance;
  return Math.max(layout.slotsX1, edge - leftmost - layout.noteInsetX);
}

// ---- Spacing ---------------------------------------------------------------------------------

// A note as the spacing sees it: how it is written and where it sits down the staff
export type SolfegeSpacedNote = Pick<
  SolfegeStaffNote,
  'beats' | 'value' | 'syllable' | 'accidental' | 'step' | 'y' | 'stem'
>;
type SpacedNote = SolfegeSpacedNote;

export interface SolfegeSpacing {
  widths: number[]; // each note's slot, in the units of the viewBox at `scale`, or in proportion to them
  syllableSize: SolfegeSyllableSize;
  scale: number;
  fits: boolean;
}

// How a phrase is spaced across the staff (see "Spacing" in the header). When a phrase's notes do
// not fit comfortably, the remedies in order (Michael, issue #3772: give up the longer notes' extra
// room before anything else):
//   1. flatten the spacing: the longer notes give up their square-root share above their least room
//      to the notes that need it, toward even spacing, as far as equal widths if it takes that
//      (spacedWidths) - the syllables at the artboards' size, the staff at full size;
//   2. only if even that cannot clear every flag, dot and syllable: the smaller syllables, whose
//      least room is less, flattened likewise;
//   3. only if that still does not fit: the staff drawn smaller, with the smaller syllables, just
//      small enough for their least room (the scale rounded down to a hundredth), down to scaleFloor;
//   4. below the floor the phrase cannot be drawn: it is drawn at the floor with its least room
//      squeezed in proportion, and `fits` says so, for the deck-wide guard to fail on.
// The slots start at `slotsX1`: the artboards' 20, or further right after a key signature or for a
// first note's accidental (firstSlotX1), the room lost taken up by the same remedies.
export function solfegeSpacing(notes: SpacedNote[], slotsX1 = layout.slotsX1): SolfegeSpacing {
  const room = layout.slotsX2 - slotsX1;
  for (const syllableSize of layout.syllableSizes) {
    const spaced = spacedWidths(notes, leastRooms(notes, syllableSize), room);
    if (spaced.widths) {
      return { widths: spaced.widths, syllableSize, scale: 1, fits: true };
    }
  }
  const syllableSize = layout.syllableSizes[layout.syllableSizes.length - 1];
  const rooms = leastRooms(notes, syllableSize);
  const { least } = spacedWidths(notes, rooms, room);
  // Drawn at `scale`, the viewBox is width / scale wide, and the slots gain all of the difference
  const justFits = layout.width / (layout.width + least.reduce((sum, width) => sum + width, 0) - room);
  const scale = Math.max(layout.scaleFloor, Math.floor(justFits * 100) / 100);
  const spaced = spacedWidths(notes, rooms, room + layout.width * (1 / scale - 1));
  return { widths: spaced.widths ?? least, syllableSize, scale, fits: spaced.widths !== undefined };
}

// The least room each note's slot needs: the distance to the next note at which nothing of the two
// comes within the clearance of the other (leastNoteDistance), nor their syllables within the
// syllables' gap; for the last note, room for everything drawn right of its head before the staff
// lines end (they run on past the last slot). Rounded up to the next hundredth and always at least
// leastRoomMargin over the distance, so the clearance holds with room to spare for the floating
// point: the distance glyphDistance finds and any measure of the drawn notes can each be a rounding
// error out, and a least room that cleared by less than that would be too close to call.
export function solfegeLeastRoom(notes: SpacedNote[], syllableSize: SolfegeSyllableSize): number[] {
  return notes.map((note, index) => {
    const next = notes[index + 1];
    const least = next
      ? Math.max(leastNoteDistance(note, next), leastSyllableDistance(note.syllable, next.syllable, syllableSize))
      : layout.noteInsetX + rightOfCentre(note, syllableSize) - (layout.lineX2 - layout.slotsX2);
    return Math.floor((least + leastRoomMargin) * 100) / 100 + 0.01;
  });
}

// The least room's margin over the distance it is rounded up from (see solfegeLeastRoom)
const leastRoomMargin = 1e-6;

// Each note's least room, and what it would be were no note to carry an accidental: the room every
// note of its length takes is the most the second asks of any of them, while the first holds for
// the note itself (see "Spacing" in the header). A note's own accidental stands left of its head, so
// only the next note's ever adds to a note's least room.
interface LeastRooms {
  own: number[];
  byLength: number[];
}

function leastRooms(notes: SpacedNote[], syllableSize: SolfegeSyllableSize): LeastRooms {
  const own = solfegeLeastRoom(notes, syllableSize);
  if (notes.every((note) => !note.accidental)) {
    return { own, byLength: own };
  }
  const plain = notes.map((note) => ({ ...note, accidental: undefined }));
  return { own, byLength: solfegeLeastRoom(plain, syllableSize) };
}

// Each note's width, from the least room of each and the square root of its beats (see the header):
// every note of one length takes the most any of them needs, and no longer length less than a
// shorter one; the room left over is shared by the square root, each note taking the larger of its
// length's least room and its share, and a note before an accidental at least its own least room
// besides. So when a note needs more than its share, every note's share
// shrinks in proportion to make room - the longer notes give up most - until, if it takes that, each
// length has only its least room. A phrase whose least room leaves room over everywhere gets the
// square roots alone, exactly as before there was a least room. `widths` is undefined when the
// least room does not fit `room`; `least` is the widths with no room to share.
function spacedWidths(
  notes: SpacedNote[],
  leastRoom: LeastRooms,
  room: number,
): { widths?: number[]; least: number[] } {
  const lengths = [...new Set(notes.map((note) => note.beats))].sort((a, b) => a - b);
  const floors = new Map(
    lengths.map((beats) => [
      beats,
      Math.max(...notes.flatMap((note, index) => (note.beats === beats ? [leastRoom.byLength[index]] : []))),
    ]),
  );
  // The width of each length when a quarter's share is `unit`
  const widthsAt = (unit: number): number[] => {
    const byLength = new Map<number, number>();
    let shorter = -Infinity;
    for (const beats of lengths) {
      const width = Math.max(floors.get(beats) as number, solfegeSlotWeight(beats) * unit, shorter);
      byLength.set(beats, width);
      shorter = width;
    }
    return notes.map((note, index) => Math.max(byLength.get(note.beats) as number, leastRoom.own[index]));
  };
  const total = (widths: number[]) => widths.reduce((sum, width) => sum + width, 0);
  const least = widthsAt(0);
  if (total(least) > room) {
    return { least };
  }
  const weights = notes.map((note) => solfegeSlotWeight(note.beats));
  const unit = room / total(weights);
  const shares = widthsAt(unit);
  if (shares.every((width, index) => width === weights[index] * unit)) {
    return { widths: weights, least };
  }
  // The total grows steadily with the unit, from the least room at 0 to more than the room at the
  // plain share: find the unit at which it is the room
  let low = 0;
  let high = unit;
  for (let pass = 0; pass < 60; pass += 1) {
    const middle = (low + high) / 2;
    if (total(widthsAt(middle)) > room) {
      high = middle;
    } else {
      low = middle;
    }
  }
  return { widths: widthsAt(low), least };
}

// The least distance between two neighbouring syllables' centres: half of each one's width and the
// gap between them, either of the two being the current note's (larger, bold)
function leastSyllableDistance(syllable: SolfegeSyllable, next: SolfegeSyllable, size: SolfegeSyllableSize): number {
  const widths = Math.max(
    solfegeSyllableWidth(syllable, size.emphasized) + solfegeSyllableWidth(next, size.regular),
    solfegeSyllableWidth(syllable, size.regular) + solfegeSyllableWidth(next, size.emphasized),
  );
  return widths / 2 + layout.syllableMinGap;
}

// How far right of its head's centre the last note reaches: its glyph as the current note, and
// half its syllable at the current note's size
function rightOfCentre(note: SpacedNote, size: SolfegeSyllableSize): number {
  const glyph = solfegeNoteGlyph({ ...note, x: 0 }, true);
  const boxes = [glyph.head.box, glyph.stem?.box, glyph.flagBox, glyph.dot?.box];
  const glyphRight = Math.max(...boxes.map((box) => box?.x2 ?? 0));
  return Math.max(glyphRight, solfegeSyllableWidth(note.syllable, size.emphasized) / 2);
}

// The least distance between two neighbouring noteheads' centres at which no part of the first
// note - head, stem, flags, dot - comes within the clearance of any part of the second, either of
// the two drawn as the current note, larger. It depends only on how each is written and where it
// sits, so it is worked out once for each such pair.
const leastNoteDistances = new Map<string, number>();

function leastNoteDistance(note: SpacedNote, next: SpacedNote): number {
  const onLine = (step: number) => ((step % 2) + 2) % 2;
  const key = [
    note.value.name,
    note.stem,
    onLine(note.step),
    note.accidental,
    next.value.name,
    next.stem,
    next.step - note.step,
    next.accidental,
  ].join();
  const known = leastNoteDistances.get(key);
  if (known !== undefined) {
    return known;
  }
  let least = 0;
  for (const [current, nextCurrent] of [
    [false, false],
    [true, false],
    [false, true],
  ]) {
    const glyph = solfegeNoteGlyph({ ...note, x: 0, y: 0 }, current);
    const nextGlyph = solfegeNoteGlyph({ ...next, x: 0, y: next.y - note.y }, nextCurrent);
    least = Math.max(least, glyphDistance(glyph, nextGlyph));
  }
  leastNoteDistances.set(key, least);
  return least;
}

// The least distance `next` must stand to the right of `glyph`, both drawn at x 0, for every point
// of `glyph`'s outline to be at least the clearance from every part of `next`. Each part of `next`
// grown by the clearance is a convex shape, so a horizontal line through a point of `glyph` crosses
// it in one stretch, and the point clears it when it is left of that stretch's left end. Each part
// is taken in turn, so that how far a point of `glyph` must move runs smoothly along each curve of
// its outline, and glyphOutlinePeak can find its tops between the samples.
function glyphDistance(glyph: SolfegeNoteGlyph, next: SolfegeNoteGlyph): number {
  const clearance = layout.clearance;
  const leftEnds: ((y: number) => number)[] = [(y) => ellipseLeftEnd(next.head, clearance, y)];
  const stem = next.stem;
  if (stem) {
    const box = stem.box;
    leftEnds.push((y) => {
      const off = Math.max(box.y1 - y, y - box.y2, 0);
      return off < clearance ? box.x1 - Math.sqrt(clearance ** 2 - off ** 2) : Infinity;
    });
  }
  const dot = next.dot;
  if (dot) {
    leftEnds.push((y) => circleLeftEnd(dot.cx, dot.cy, dot.r + clearance, y));
  }
  for (const point of next.flagOutlines.flat()) {
    leftEnds.push((y) => circleLeftEnd(point.x, point.y, clearance, y));
  }
  // An accidental's pieces are convex, so each edge grown by the clearance is a convex capsule, and
  // together they cover the piece grown by it
  for (const edge of (next.accidental?.parts ?? []).flatMap(polygonEdges)) {
    leftEnds.push((y) => segmentLeftEnd(edge[0], edge[1], clearance, y));
  }
  const { curves, points } = solfegeGlyphOutline(glyph);
  let least = 0;
  for (const leftEnd of leftEnds) {
    const need = (point: SolfegePoint) => point.x - leftEnd(point.y);
    for (const point of points) {
      least = Math.max(least, need(point));
    }
    for (const curve of curves) {
      least = Math.max(least, glyphOutlinePeak(curve, need));
    }
  }
  return least;
}

function polygonEdges(polygon: SolfegePoint[]): [SolfegePoint, SolfegePoint][] {
  return polygon.map((point, index) => [point, polygon[(index + 1) % polygon.length]]);
}

// Where a horizontal line at y first meets the points within r of the segment from a to b (a
// capsule), or Infinity if it misses: the nearer of its end circles and of the band either side of
// the segment, the band's stretch along the line found from its four straight edges
function segmentLeftEnd(a: SolfegePoint, b: SolfegePoint, r: number, y: number): number {
  const ends = Math.min(circleLeftEnd(a.x, a.y, r, y), circleLeftEnd(b.x, b.y, r, y));
  const length = Math.hypot(b.x - a.x, b.y - a.y);
  if (length === 0) {
    return ends;
  }
  const along = { x: (b.x - a.x) / length, y: (b.y - a.y) / length };
  const across = { x: -along.y, y: along.x };
  let low = -Infinity;
  let high = Infinity;
  // Keeps the x at which min <= (x - a.x) * axis.x + (y - a.y) * axis.y <= max
  const keep = (axis: SolfegePoint, min: number, max: number) => {
    const offset = (y - a.y) * axis.y - a.x * axis.x;
    if (Math.abs(axis.x) < 1e-12) {
      if (offset < min || offset > max) {
        low = Infinity;
      }
      return;
    }
    const [from, to] = [(min - offset) / axis.x, (max - offset) / axis.x];
    low = Math.max(low, Math.min(from, to));
    high = Math.min(high, Math.max(from, to));
  };
  keep(along, 0, length);
  keep(across, -r, r);
  return low <= high ? Math.min(ends, low) : ends;
}

// Where a horizontal line at y first meets a circle, or Infinity if it misses
function circleLeftEnd(cx: number, cy: number, r: number, y: number): number {
  const dy = y - cy;
  return Math.abs(dy) < r ? cx - Math.sqrt(r * r - dy * dy) : Infinity;
}

// Where a horizontal line at y first meets a notehead grown by `grow` on both radii, or Infinity
function ellipseLeftEnd(head: SolfegeNoteGlyph['head'], grow: number, y: number): number {
  const radians = (head.rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const a2 = (head.rx + grow) ** 2;
  const b2 = (head.ry + grow) ** 2;
  // the head's equation along the line, in x from the centre: qa x² + qb x + qc = 0
  const v = y - head.cy;
  const qa = (cos * cos) / a2 + (sin * sin) / b2;
  const qb = 2 * v * sin * cos * (1 / a2 - 1 / b2);
  const qc = v * v * ((sin * sin) / a2 + (cos * cos) / b2) - 1;
  const discriminant = qb * qb - 4 * qa * qc;
  return discriminant > 0 ? head.cx + (-qb - Math.sqrt(discriminant)) / (2 * qa) : Infinity;
}

// A stretch of a note's outline: its point at t from 0 to 1, first looked at in `samples` even steps,
// and, when it is closed, coming round to its start again at 1
export interface SolfegeOutlineCurve {
  at: (t: number) => SolfegePoint;
  samples: number;
  closed: boolean;
}

const outlineSamples = 64;

// The outline of everything a note draws: its head's edge, its stem's right side, its dot's edge and
// its accidental's pieces' edges as curves, and its flags as the points of their outlines (as
// solfegeNoteGlyph gives them, and as the next note's flags are measured against). The samples are
// close enough together that a part of the next note grown by the clearance - two units across at
// the least - always spans more than one of them.
export function solfegeGlyphOutline(glyph: SolfegeNoteGlyph): {
  curves: SolfegeOutlineCurve[];
  points: SolfegePoint[];
} {
  const { cx, cy, rx, ry, rotation } = glyph.head;
  const radians = (rotation * Math.PI) / 180;
  const curves: SolfegeOutlineCurve[] = [
    {
      at: (t) => {
        const x = rx * Math.cos(2 * Math.PI * t);
        const y = ry * Math.sin(2 * Math.PI * t);
        return {
          x: cx + x * Math.cos(radians) - y * Math.sin(radians),
          y: cy + x * Math.sin(radians) + y * Math.cos(radians),
        };
      },
      samples: outlineSamples,
      closed: true,
    },
  ];
  if (glyph.stem) {
    const box = glyph.stem.box;
    curves.push({
      at: (t) => ({ x: box.x2, y: box.y1 + (box.y2 - box.y1) * t }),
      samples: Math.ceil((box.y2 - box.y1) / 0.5),
      closed: false,
    });
  }
  const dot = glyph.dot;
  if (dot) {
    curves.push({
      at: (t) => ({ x: dot.cx + dot.r * Math.cos(2 * Math.PI * t), y: dot.cy + dot.r * Math.sin(2 * Math.PI * t) }),
      samples: outlineSamples / 2,
      closed: true,
    });
  }
  for (const [from, to] of (glyph.accidental?.parts ?? []).flatMap(polygonEdges)) {
    curves.push({
      at: (t) => ({ x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t }),
      samples: Math.max(1, Math.ceil(Math.hypot(to.x - from.x, to.y - from.y) / 0.5)),
      closed: false,
    });
  }
  return { curves, points: glyph.flagOutlines.flat() };
}

// How finely the stretch either side of a sample is looked at again, and how many times the stretch
// around the best of those is then narrowed
const refineSamples = 16;
const refinePasses = 30;

// The most `need` reaches along a curve. The samples alone fell short: a note's head is curved, so
// the point of it nearest the next note can lie between two samples, and And Can It Be's opening
// (re, a sixteenth, a step below mi drawn as the current note, with do on C5) stood 0.9975 from mi's
// stem where every sample cleared it (#3772). Along a curve, against one convex part, `need` rises
// and falls smoothly wherever the part is in reach, so each of its tops lies within a sample of a
// sample at least as high as its neighbours: each such sample has the stretch either side of it
// sampled again finely, and the stretch around the best of those narrowed by golden sections to the
// top. The samples are all still taken, so a least room only ever grows by what they missed.
function glyphOutlinePeak(curve: SolfegeOutlineCurve, need: (point: SolfegePoint) => number): number {
  const { samples, closed } = curve;
  const at = (t: number) => need(curve.at(closed ? t : Math.min(1, Math.max(0, t))));
  const values = Array.from({ length: closed ? samples : samples + 1 }, (_unused, index) => at(index / samples));
  const valueAt = (index: number) =>
    closed ? values[(index + values.length) % values.length] : (values[index] ?? -Infinity);
  let peak = Math.max(...values);
  for (const [index, value] of values.entries()) {
    if (value === -Infinity || value < valueAt(index - 1) || value < valueAt(index + 1)) {
      continue;
    }
    const fine = 1 / (samples * refineSamples);
    let best = value;
    let bestT = index / samples;
    for (let step = -refineSamples; step <= refineSamples; step += 1) {
      const t = index / samples + step * fine;
      const fineValue = at(t);
      if (fineValue > best) {
        best = fineValue;
        bestT = t;
      }
    }
    let low = bestT - fine;
    let high = bestT + fine;
    for (let pass = 0; pass < refinePasses; pass += 1) {
      const lower = high - (high - low) / goldenRatio;
      const upper = low + (high - low) / goldenRatio;
      const lowerValue = at(lower);
      const upperValue = at(upper);
      best = Math.max(best, lowerValue, upperValue);
      if (lowerValue < upperValue) {
        low = lower;
      } else {
        high = upper;
      }
    }
    peak = Math.max(peak, best);
  }
  return peak;
}

const goldenRatio = (1 + Math.sqrt(5)) / 2;

function slotsFor(
  widths: number[],
  startsMs: number[],
  endsMs: number[],
  slotsX1: number,
  slotsX2: number,
): SolfegeSlot[] {
  const total = widths.reduce((sum, width) => sum + width, 0);
  const unit = (slotsX2 - slotsX1) / total;
  let x = slotsX1;
  return widths.map((width, index) => {
    const x1 = x;
    // the last slot ends exactly at slotsX2, whatever the floating point made of the sum
    x = index === widths.length - 1 ? slotsX2 : x1 + width * unit;
    return { x1, x2: x, startMs: startsMs[index], endMs: endsMs[index] };
  });
}

function ledgerHalfWidthAt(xs: number[], index: number): number {
  const gaps = [xs[index] - xs[index - 1], xs[index + 1] - xs[index]].filter((gap) => Number.isFinite(gap));
  const nearest = Math.min(Infinity, ...gaps);
  const { rx, ry } = layout.emphasizedNotehead;
  const leastHalfWidth = ellipseExtent(rx, ry, layout.noteheadRotation).halfWidth + layout.ledgerOverhang;
  return Math.max(leastHalfWidth, Math.min(layout.ledgerMaxHalfWidth, nearest / 2 - layout.ledgerClearance));
}

function stepYIn(band: SolfegeStaffBand, step: number): number {
  return bandPadding + (band.maxStep - step) * stepHeight;
}

// Where a step sits in the SVG: the band's top step at the top padding, y growing downwards
export function solfegeStepY(geometry: SolfegeStaffGeometry, step: number): number {
  return stepYIn(geometry.band, step);
}

// ---- Time across -----------------------------------------------------------------------------

// Where a moment of the phrase is across the staff, `intoMs` on phraseNoteSpans' scale: piecewise
// linear through the slots, clamped to the phrase (see the header). Never NaN: a moment that is not
// a number is put at the phrase's start.
export function solfegeTimeX(geometry: SolfegeStaffGeometry, intoMs: number): number {
  const notes = geometry.notes;
  if (notes.length === 0) {
    return geometry.slotsX1;
  }
  if (Number.isNaN(intoMs) || intoMs <= 0) {
    return notes[0].slot.x1;
  }
  if (intoMs >= geometry.lengthMs) {
    return notes[notes.length - 1].slot.x2;
  }
  const slot = notes.find((note) => intoMs < note.slot.endMs)?.slot ?? notes[notes.length - 1].slot;
  const fraction = (intoMs - slot.startMs) / (slot.endMs - slot.startMs);
  return slot.x1 + fraction * (slot.x2 - slot.x1);
}

// ---- Pitch down ------------------------------------------------------------------------------

// The continuous step a pitch (a fractional midi number) is drawn at: between the steps of the two
// notes of the phrase's scale around it, in proportion to the semitones between them (see the
// header). Not clamped. A pitch that is not a number is drawn on do.
export function solfegePitchStep(geometry: SolfegeStaffGeometry, midi: number): number {
  return pitchStep(geometry.homeMidi, geometry.doStep, geometry.mode, midi);
}

// How far a degree of the mode's own scale is from do: the major scale's, with minor's third, sixth
// and seventh a semitone lower
function scaleSemitones(mode: SolfegeMode, degree: number): number {
  return semitonesFromDo(degree) + solfegeScaleAlteration(mode, degree);
}

function pitchStep(homeMidi: number, doStep: number, mode: SolfegeMode, midi: number): number {
  if (!Number.isFinite(midi)) {
    return doStep;
  }
  const semitones = midi - homeMidi;
  let degree = Math.floor(semitones / 12) * 7;
  while (scaleSemitones(mode, degree + 1) <= semitones) {
    degree += 1;
  }
  while (scaleSemitones(mode, degree) > semitones) {
    degree -= 1;
  }
  const below = scaleSemitones(mode, degree);
  const fraction = (semitones - below) / (scaleSemitones(mode, degree + 1) - below);
  return doStep + degree + fraction;
}

// Where a sung pitch is drawn, clamped to the band: the dot's height and every point of the trace
export function solfegePitchY(geometry: SolfegeStaffGeometry, midi: number): number {
  return solfegeStepY(geometry, clampToBand(solfegePitchStep(geometry, midi), geometry.band));
}

// ---- Lanes and syllables ---------------------------------------------------------------------

export interface SolfegeRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

// A note's target lane: one step tall, centred on the note's pitch as the line draws it, across its
// slot. A scale note's lane is centred on its head; a chromatic note's, at its true pitch, a little
// above or below the head that sits on its degree's line (see the header).
export function solfegeLaneRect(note: Pick<SolfegeStaffNote, 'slot' | 'laneY'>): SolfegeRect {
  return {
    x: note.slot.x1 + layout.laneInsetX,
    y: note.laneY - layout.laneHeight / 2,
    width: note.slot.x2 - note.slot.x1 - 2 * layout.laneInsetX,
    height: layout.laneHeight,
  };
}

// Whether any two neighbouring syllables would touch at this size, either of them being the
// current note's (larger, bold): centred on their noteheads, they need half of each width plus
// the minimum gap between their centres
export function solfegeSyllablesCollide(notes: SolfegeStaffNote[], size: SolfegeSyllableSize): boolean {
  return notes.slice(1).some((note, index) => {
    const before = notes[index];
    const room = note.x - before.x;
    const needed = Math.max(
      solfegeSyllableWidth(before.syllable, size.emphasized) + solfegeSyllableWidth(note.syllable, size.regular),
      solfegeSyllableWidth(before.syllable, size.regular) + solfegeSyllableWidth(note.syllable, size.emphasized),
    );
    return needed / 2 + layout.syllableMinGap > room;
  });
}

// ---- Note glyphs -----------------------------------------------------------------------------

// A box in the SVG's units, for the tests' clearances
export interface SolfegeBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface SolfegePoint {
  x: number;
  y: number;
}

export interface SolfegeNoteGlyph {
  value: SolfegeNoteValue;
  scale: number; // 1, or larger for the current note
  head: {
    cx: number;
    cy: number;
    rx: number;
    ry: number;
    rotation: number;
    open: boolean;
    // An open head's ring, outer ellipse and hole, to fill with the even-odd rule
    ringD?: string;
    box: SolfegeBox;
  };
  stem?: { x: number; y1: number; y2: number; box: SolfegeBox };
  flagSide: 'right'; // a flag always stands to the right of its stem, whichever way the stem points
  flags: string[]; // one closed SVG path each, the first at the stem's end
  // Each flag's outline as points along its curves, for the tests' clearances
  flagOutlines: SolfegePoint[][];
  flagBox?: SolfegeBox;
  dot?: { cx: number; cy: number; r: number; box: SolfegeBox };
  accidental?: SolfegeSign; // a chromatic note's sharp, flat or natural, just left of its head
}

// One flag hanging from a stem's end at (0, 0), +y toward the head, in the units of a normal note,
// as one move and three cubic Béziers: a root on the stem, an outer edge swelling right and curving
// back in to a point, and an inner edge returning to the stem - the outline of an engraved eighth's
// flag, kept short to suit this staff's short stems. Mirrored top to bottom on a stem that points
// down, so it still hangs toward the head and stands to the stem's right.
type FlagOutline = [number, number][][];

const flagOutline: FlagOutline = [
  [[0, 0]],
  [
    [0.4, 4.2],
    [3.6, 6.2],
    [6.3, 9.2],
  ],
  [
    [9.0, 12.2],
    [9.4, 16.4],
    [7.0, 20.5],
  ],
  [
    [7.6, 16.2],
    [5.6, 12.0],
    [0, 8.4],
  ],
];

// A sixteenth's upper flag is shorter, as engraved: it tucks over the lower one, whose tail carries
// the hook, and the two read as two flags rather than one heavier one
const upperFlagOutline: FlagOutline = flagOutline.map((points) =>
  points.map(([x, y]): [number, number] => [x * 0.95, y * 0.62]),
);

function flagOutlineFor(flag: number, count: number): FlagOutline {
  return flag < count - 1 ? upperFlagOutline : flagOutline;
}

const flagSamples = 16;

// Points along a flag's outline, in its own units
function flagCurvePoints(outline: FlagOutline): [number, number][] {
  const points: [number, number][] = [];
  let from = outline[0][0];
  for (const [c1, c2, to] of outline.slice(1)) {
    for (let step = 0; step < flagSamples; step += 1) {
      const t = step / flagSamples;
      const u = 1 - t;
      const bezier = (axis: 0 | 1) =>
        u * u * u * from[axis] + 3 * u * u * t * c1[axis] + 3 * u * t * t * c2[axis] + t * t * t * to[axis];
      points.push([bezier(0), bezier(1)]);
    }
    from = to;
  }
  return points;
}

// A rotated ellipse's half width and half height
function ellipseExtent(rx: number, ry: number, degrees: number): { halfWidth: number; halfHeight: number } {
  const radians = (degrees * Math.PI) / 180;
  return {
    halfWidth: Math.hypot(rx * Math.cos(radians), ry * Math.sin(radians)),
    halfHeight: Math.hypot(rx * Math.sin(radians), ry * Math.cos(radians)),
  };
}

// A closed rotated ellipse as two SVG arcs
function ellipsePath(cx: number, cy: number, rx: number, ry: number, degrees: number): string {
  const radians = (degrees * Math.PI) / 180;
  const dx = rx * Math.cos(radians);
  const dy = rx * Math.sin(radians);
  const arc = `A${fixed(rx)} ${fixed(ry)} ${degrees} 1 0`;
  return `M${fixed(cx - dx)} ${fixed(cy - dy)}${arc} ${fixed(cx + dx)} ${fixed(cy + dy)}${arc} ${fixed(cx - dx)} ${fixed(cy - dy)}Z`;
}

function fixed(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return String(rounded === 0 ? 0 : rounded);
}

// Where every part of a note is drawn: its head (filled or a ring), its stem, its flags, its dot and
// its accidental, scaled up with the head when it is the current note (emphasized)
export function solfegeNoteGlyph(
  note: Pick<SolfegeStaffNote, 'x' | 'y' | 'step' | 'value' | 'stem' | 'accidental'>,
  emphasized: boolean,
): SolfegeNoteGlyph {
  const value = note.value;
  const head = emphasized ? layout.emphasizedNotehead : layout.normalNotehead;
  const scale = head.rx / layout.normalNotehead.rx;
  const rotation = layout.noteheadRotation;
  const extent = ellipseExtent(head.rx, head.ry, rotation);
  const open = value.head === 'open';
  const hole = layout.openHole;
  const glyph: SolfegeNoteGlyph = {
    value,
    scale,
    head: {
      cx: note.x,
      cy: note.y,
      rx: head.rx,
      ry: head.ry,
      rotation,
      open,
      ringD: open
        ? ellipsePath(note.x, note.y, head.rx, head.ry, rotation) +
          ellipsePath(note.x, note.y, head.rx * hole.rxShare, head.ry * hole.ryShare, hole.rotation)
        : undefined,
      box: {
        x1: note.x - extent.halfWidth,
        y1: note.y - extent.halfHeight,
        x2: note.x + extent.halfWidth,
        y2: note.y + extent.halfHeight,
      },
    },
    flagSide: 'right',
    flags: [],
    flagOutlines: [],
  };
  if (value.stem) {
    const up = note.stem === 'up';
    const x = note.x + (up ? 1 : -1) * (head.rx - layout.stemInsetX);
    const endY = note.y + (up ? -1 : 1) * scale * layout.stemLength;
    const halfWidth = layout.stemWidth / 2;
    glyph.stem = {
      x,
      y1: note.y,
      y2: endY,
      box: { x1: x - halfWidth, y1: Math.min(note.y, endY), x2: x + halfWidth, y2: Math.max(note.y, endY) },
    };
    if (value.flags > 0) {
      // toward the head: down the page from an up stem's top, up it from a down stem's foot
      const towardHead = up ? 1 : -1;
      const place = (flag: number, [px, py]: [number, number]): SolfegePoint => ({
        x: x + scale * px,
        y: endY + towardHead * scale * (py + flag * layout.flagSpacing),
      });
      const at = (flag: number, point: [number, number]) => {
        const placed = place(flag, point);
        return `${fixed(placed.x)} ${fixed(placed.y)}`;
      };
      const flagIndexes = Array.from({ length: value.flags }, (_unused, flag) => flag);
      glyph.flags = flagIndexes.map((flag) =>
        flagOutlineFor(flag, value.flags)
          .map((points, index) =>
            index === 0 ? `M${at(flag, points[0])}` : `C${points.map((point) => at(flag, point)).join(' ')}`,
          )
          .join('')
          .concat('Z'),
      );
      glyph.flagOutlines = flagIndexes.map((flag) =>
        flagCurvePoints(flagOutlineFor(flag, value.flags)).map((point) => place(flag, point)),
      );
      // The box of every flag's control points, which hold the curves
      const corners = flagIndexes.flatMap((flag) =>
        flagOutlineFor(flag, value.flags)
          .flat()
          .map((point) => place(flag, point)),
      );
      glyph.flagBox = {
        x1: x - halfWidth,
        y1: Math.min(...corners.map((corner) => corner.y)),
        x2: Math.max(...corners.map((corner) => corner.x)),
        y2: Math.max(...corners.map((corner) => corner.y)),
      };
    }
  }
  if (value.dotted) {
    const onLine = ((note.step % 2) + 2) % 2 === 0;
    const r = scale * layout.augmentationDotRadius;
    const cx = note.x + scale * layout.augmentationDotX;
    const cy = onLine ? note.y - layout.stepHeight : note.y;
    glyph.dot = { cx, cy, r, box: { x1: cx - r, y1: cy - r, x2: cx + r, y2: cy + r } };
  }
  if (note.accidental) {
    // on the note's own line or space, its right edge the gap left of the head's widest point
    glyph.accidental = solfegeSign(
      note.accidental,
      note.x - extent.halfWidth - scale * layout.accidentalGap,
      note.y,
      scale * layout.accidentalUnit,
    );
  }
  return glyph;
}
